"use client";

import React from "react";
import { ArrowRight, ShieldCheck, ThermometerSnowflake, Clock, Truck } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const keyHighlights = [{
  icon: ThermometerSnowflake,
  title: "Temperature control",
  desc: "Verified specifications for the 6×8, 7×12, and 7×16 trailers include one standard 115-volt, 15-amp electrical circuit and temperatures between −10°F and 40°F when properly connected and operated. Requirements and specifications for the 53-foot reefer trailer are confirmed separately."
}, {
  icon: Truck,
  title: "Delivery Area",
  desc: "Serving Southern California and our listed Las Vegas-area communities."
}, {
  icon: Clock,
  title: "Rental Terms",
  desc: "Rental terms are based on your requested delivery and pickup dates and are included with each quote"
}, {
  icon: ShieldCheck,
  title: "Optional Add-ons",
  desc: "Optional Honda 3000 generators and interior shelving are available. Pricing and availability are confirmed with each quote"
}];
export default function Hero() {
  const shouldReduceMotion = useReducedMotion();
  const baseTransition = {
    duration: shouldReduceMotion ? 0.01 : 0.45,
    ease: [0.16, 1, 0.3, 1]
  };
  return <section id="hero" data-nav="light" className="relative overflow-hidden bg-background py-14 md:py-20 lg:py-24 border-b border-border">
      {/* Structural background grid accent */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.03] [background-image:linear-gradient(to_right,hsl(222_47%_11%)_1px,transparent_1px),linear-gradient(to_bottom,hsl(222_47%_11%)_1px,transparent_1px)] [background-size:40px_40px]" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Left Column: Text & CTAs */}
          <div className="lg:col-span-7 flex flex-col items-start space-y-6">
            {/* Operational Status Pill */}
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            ...baseTransition,
            delay: 0
          }} className="max-w-full">
              <Badge variant="outline" className="rounded-full px-3.5 py-1.5 border-border bg-card text-foreground text-xs font-semibold shadow-2xs gap-2 whitespace-normal max-w-full text-left">
                <span className="inline-block size-2 shrink-0 rounded-full bg-chart-2 animate-pulse" />
                <span>Southern California & Las Vegas-Area Cold Storage</span>
              </Badge>
            </motion.div>

            {/* Headline */}
            <motion.h1 initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            ...baseTransition,
            delay: 0.05
          }} className="font-default text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-foreground tracking-tight leading-[1.1]">
              Mobile Refrigerated &amp; Freezer Trailer Rentals
            </motion.h1>

            {/* Subheading */}
            <motion.p initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            ...baseTransition,
            delay: 0.1
          }} className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl font-normal leading-relaxed">
              Fridge Fleet provides mobile refrigerated and freezer trailer rentals—from compact towable units to a 53-foot reefer trailer—for temporary cold storage, food service, catering, events, commercial operations, and overflow needs throughout Southern California and the Las Vegas area.
            </motion.p>

            {/* Dual CTA Cluster */}
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            ...baseTransition,
            delay: 0.16
          }} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5 w-full sm:w-auto pt-2">
              <Button asChild size="lg" className="bg-primary text-primary-foreground font-semibold px-7 h-12 shadow-sm transition-[background-color,transform] duration-150 active:scale-[0.98]">
                <Link to="/ContactRequestAQuotePage" className="inline-flex items-center justify-center gap-2">
                  <span>Request a Quote</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <a href="tel:2135218821" className="inline-flex items-center justify-center gap-2 px-5 h-12 rounded-md border border-border bg-muted/60 text-muted-foreground font-medium text-sm">
                <span>213-521-8821</span>
              </a>
            </motion.div>

            {/* Quick spec indicators */}
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            ...baseTransition,
            delay: 0.22
          }} className="pt-4 flex flex-wrap items-center gap-y-2 gap-x-6 text-xs text-muted-foreground border-t border-border/80 w-full">
              <div className="flex items-center gap-1.5">
                <span className="size-1.5 shrink-0 rounded-full bg-chart-1" />
                <span>Multiple Trailer Size Options</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="size-1.5 shrink-0 rounded-full bg-chart-2" />
                <span>Availability and rental terms provided with quote</span>
              </div>
            </motion.div>
          </div>

          {/* Right Column: Hero Visual Container & Highlights */}
          <div className="lg:col-span-5 flex flex-col space-y-4">
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1,
            scale: 1
          } : {
            opacity: 0,
            scale: 0.98
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            ...baseTransition,
            delay: 0.12
          }} className="relative rounded-lg overflow-hidden border border-border bg-card shadow-md">
              <div className="relative aspect-[16/11] w-full overflow-hidden bg-muted">
                <img className="w-full h-full object-cover" src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796053/img/ChatGPT-Image-Sep-12-2026-06_46_23-PM-1.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796053/img/ChatGPT-Image-Sep-12-2026-06_46_23-PM-1.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796053/img/ChatGPT-Image-Sep-12-2026-06_46_23-PM-1.png-wvc-sizes" alt="Fridge Fleet mobile refrigeration trailer parked at an outdoor event venue." />
              </div>
            </motion.div>

            {/* 2x2 Micro Specs Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              {keyHighlights.map((item, i) => {
              const IconComponent = item.icon;
              return <div key={item.title} data-index={i} className="p-3.5 rounded-md bg-card border border-border shadow-2xs flex flex-col space-y-1 hover:border-primary/40 transition-[border-color,box-shadow] duration-200">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 rounded bg-muted text-foreground">
                        <IconComponent className="size-4 text-chart-1" />
                      </div>
                      <h2 className="text-sm font-semibold text-foreground tracking-normal font-default">
                        {item.title}
                      </h2>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed pl-7">
                      {item.desc}
                    </p>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}